import { Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DropdownPosition } from '@ng-select/ng-select';
import { AddTagFn, GroupValueFn } from '@ng-select/ng-select/lib/ng-select.component';
import { Any } from 'currency.js';
import { Observable, Subject, Subscription } from 'rxjs/dist/types';

@Component({
  selector: 'via-lazy-select',
  templateUrl: './lazy-select.component.html',
  styleUrls: ['./lazy-select.component.css'],
})
export class LazySelectComponent implements OnChanges {
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() markFirst = true;
  @Input() placeholder: string;
  @Input() notFoundText: string = "Nenhum item encontrado";
  @Input() typeToSearchText: string;
  @Input() addTagText: string;
  @Input() loadingText: string = "Carregando...";
  @Input() clearAllText: string = "Limpar";
  @Input() appearance: string;
  @Input() dropdownPosition: DropdownPosition = 'auto';
  @Input() appendTo: string;
  @Input() loading = false;
  @Input() closeOnSelect = true;
  @Input() hideSelected = false;
  @Input() selectOnTab = false;
  @Input() openOnEnter: boolean;
  @Input() maxSelectedItems: number;
  @Input() groupBy: string | ((_: Any) => unknown);
  @Input() groupValue: GroupValueFn;
  @Input() bufferAmount = 4;
  @Input() virtualScroll: boolean;
  @Input() selectableGroup = false;
  @Input() selectableGroupAsModel = true;
  @Input() searchFn = null;
  @Input() trackByFn = null;
  @Input() clearOnBackspace = true;
  @Input() labelForId = null;
  @Input() inputAttrs: { [key: string]: string } = {};
  @Input() tabIndex: number;
  @Input() readonly = false;
  @Input() searchWhileComposing = true;
  @Input() minTermLength = 0;
  @Input() editableSearchTerm = false;
  @Input() keyDownFn = (_: KeyboardEvent) => true;

  @Input() @HostBinding('class.ng-select-typeahead') typeahead: Subject<string>;
  @Input() @HostBinding('class.ng-select-multiple') multiple = false;
  @Input() @HostBinding('class.ng-select-taggable') addTag: boolean | AddTagFn = false;
  @Input() @HostBinding('class.ng-select-searchable') searchable = true;
  @Input() @HostBinding('class.ng-select-clearable') clearable = true;
  @Input() @HostBinding('class.ng-select-opened') isOpen?: boolean = false;

  @Input() public items$: Observable<unknown[]>;
  @Input() public lazyLabelFunction: (item: unknown) => string | null;
  @Input() public customFormControl: FormControl;
  @Input() public label: string;
  @Input() public required: boolean;

  public items: unknown[] = [];

  private subscription: Subscription;

  private readonly defaultBindLabel = "lazyLabel";

  public ngOnChanges(simplesChanges: SimpleChanges): void {
    if (!simplesChanges.items$.firstChange) {
      this.reset();
    }
  }

  public onOpen(): void {
    if (this.items$ == null || this.items?.length > 0 || this.subscription != null) {
      return;
    }

    this.loading = true;

    this.subscription = this.items$?.subscribe({
      next: (items) => {
        this.reset();

        if (this.lazyLabelFunction != null) {
          if (this.bindLabel != null) {
            throw new Error("Não é possível utilizar 'lazyLabelFunction' e 'bindLabel' ao mesmo tempo.");
          }

          items = items.map((item) => {
            if (item != null && typeof item === "object" && !Array.isArray(item)) {
              return { ...item, lazyLabel: this.lazyLabelFunction(item) };
            }

            return item;
          });

          this.bindLabel = this.defaultBindLabel;
        }

        this.items = items;
      },
      error: (error) => {
        console.error('Erro ao carregar os itens:', error);
        this.reset();
      },
      complete: () => {
        this.reset();
      }
    });
  }

  private reset(): void {
    this.loading = false;
    this.items = [];
    this.subscription?.unsubscribe();
    this.subscription = null;

    if (this.bindLabel === this.defaultBindLabel) {
      this.bindLabel = null;
    }
  }
}
